import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import FormBuilder from "../../../components/formBuilder/FormBuilder";
import PeakHourConfig from '../../../components/peakHourConfig/PeakHourConfig';
import "./CityConfigForm.scss";
import LocationDropdown from "../../../components/LocationDropdown/LocationDropdown";
import { useDispatch, useSelector } from 'react-redux';
import rateService from '../../../services/rateService';
import { getCar } from '../../../redux/actions/UserCar';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getChooseYourService } from "redux/actions/UserChooseYourService";
import PackageConfig from "./PackageConfig";

const getRateTypeByRideType = (rideType) => {
  if (rideType === "Rental" || rideType === "Outstation") {
    return "package";
  }
  return "flat_per_km";
};


const transformDutyTypeOptions = (dutyTypes) => {
  if (!dutyTypes) return [];
  return dutyTypes.map(duty => ({
    value: duty.name,
    label: duty.name,
    tripType: duty.tripType
  }));
};

// Add DEFAULT_LOCATION constant at the top
const DEFAULT_LOCATION = "Bengaluru"; // Default location to use if none is selected

export default function CityConfigForm() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const [rateType, setRateType] = useState(state.rateType || "");
  const [formData, setFormData] = useState({
    locationId: null,
    rideType: state.rideType || "",
    carType: state.carType || "",
    rateType: state.rateType || "",
    baseRate: "",
    surCharge: "", // Changed from surcharge to surCharge
    platformCharge: "",
    waitingTime: "", // Added waiting time rate
    bufferTime: "",
    extraKmCharge: "",
    graceKm: "",
    extraTimeCharge: "",
    graceTime: "",
    kmCharge: "",
    rateConfig: { // Add rateConfig section
      base_rate_per_km: ""
    },
    peakHours: []
  });
  const [peakHours, setPeakHours] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [carOptions, setCarOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [rateCardId, setRateCardId] = useState(null); // Add state for rate card ID
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false); // Add hasChanges state
  const [hasExistingConfig, setHasExistingConfig] = useState(false); // Add state for existing config
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedRideType, setSelectedRideType] = useState('');
  const [selectedCarType, setSelectedCarType] = useState('');

  //const locationFromRedux = useSelector((state) => state?.locationDropdown?.selectedLocation);
  const carData = useSelector((state) => state?.userCar?.userCar?.data?.body);
  const dutyTypeOptions = useSelector(
    (state) => state?.userChooseYourService?.userChooseYourService?.data
  );

  const rideTypeOptions = React.useMemo(() =>
    transformDutyTypeOptions(dutyTypeOptions),
    [dutyTypeOptions]
  );

  useEffect(() => {
    dispatch(getCar());

    const data = {
      id: localStorage.getItem("companyId"),
      type: "Company",
      businessType: 1,
    };

    dispatch(getChooseYourService(data));
  }, []);

  // Replace the existing getRateTypeOptions function
  const getRateTypeOptions = () => {

    if (selectedRideType.startsWith("Ride") || selectedRideType.startsWith("Airport")) {
      // Only show km option for Ride and Airport
      return [
        { value: "km", label: "/km", disabled: false }
      ];
    }

    if (selectedRideType === "Rental" || selectedRideType === "Outstation") {
      // Only show slab and package for Rental and Outstation
      return [
        { value: "package", label: "Package", disabled: false }
      ];
    }

    // Default: show all options as disabled when no ride type is selected
    return [
      { value: "km", label: "/km", disabled: true },
      { value: "package", label: "Package", disabled: true }
    ];
  };

  // Update getFormConfig to use current values
  const getFormConfig = () => ({
    title: "Configure Rates",
    formField: [
      {
        title: "Basic configuration",
        formFields: [
          {
            name: "rateType",
            // label: "Rate Type",
            inputType: "select",
            options: getRateTypeOptions(),
            value: formData.rateType, // Set current value
            validation: { required: "Rate Type is required" }
          },
          {
            name: "baseRate",
            label: "Base Rate",
            inputType: "number",
            prefix: "₹",
            validation: {
              required: "Base Rate is required",
              min: { value: 1, message: "Must be greater than 0" }
            }
          },
          {
            name: "surCharge",
            label: "Surcharge",
            inputType: "number",
            prefix: "₹",
            validation: {

              min: { value: 1, message: "Must be greater than 0" }
            }
          },
          {
            name: "platformCharge",
            label: "Platform Charge",
            inputType: "number",
            prefix: "₹",
            validation: {

              min: { value: 1, message: "Must be greater than 0" }
            }
          }
        ]
      }
    ],
    selectedRideType,
    rateType
  });

  // Update handleSaveFormData to store rateCardId after creation
  const handleSaveFormData = async () => {
    const locationToUse = formData.locationId || selectedLocation?.id;

    if (!locationToUse) {
      toast.warning('Please select a location');
      return;
    }

    setIsSaving(true);
    try {
      const result = await rateService.createRateCard(formData, locationToUse, dutyTypeOptions);
      const newRateCardId = result?.data?.created?.[0]?.id;
      if (result.success) {
        setHasChanges(false);
        setHasExistingConfig(true);
        setRateCardId(newRateCardId);
      }
    } catch (error) {
      console.error('Error saving rate card:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormChange = (updatedData) => {
    setFormData(prev => {
      const hasActualChanges = Object.keys(updatedData).some(key => {
        if (key === 'packages') {
          const prevPackages = prev[key] || [];
          const updatedPackages = updatedData[key] || [];
          return JSON.stringify(prevPackages) !== JSON.stringify(updatedPackages);
        }
        return String(updatedData[key]) !== String(prev[key]);
      });

      if (hasActualChanges) {
        setHasChanges(true);
      }

      return {
        ...prev,
        ...updatedData
      };
    });
  };


  // Add handleUpdateFormData function
  const handleUpdateFormData = async () => {
    if (!rateCardId) {
      return;
    }

    setIsUpdating(true);
    try {
      const result = await rateService.updateRateCard(rateCardId, formData);
      if (result.success) {
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error updating rate card:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Update useEffect for car options
  useEffect(() => {
    if (carData && Array.isArray(carData)) {
      const options = carData.map(car => ({
        value: car.id,
        label: car.carType
      }));
      setCarOptions(options);
      if (!selectedCarType && options.length > 0) {
        setSelectedCarType(options[0].value); // Set first car type as default
        if (!selectedRideType) {
          setSelectedRideType("Ride"); // Default ride type if not set
        }
      }
    }
  }, [carData, selectedCarType]);

  // Add this useEffect after your other useEffects
  useEffect(() => {
    // Auto-select appropriate rate type when ride type changes
    if (selectedRideType) {
      if (selectedRideType.startsWith("Ride") || selectedRideType.startsWith("Airport")) {
        setFormData(prev => ({ ...prev, rateType: "km" }));
        setRateType("km");
      } else if (selectedRideType.startsWith("Rental") || selectedRideType.startsWith("Outstation")) {
        setFormData(prev => ({ ...prev, rateType: "package" }));
        setRateType("package");
      }
      setHasChanges(true);
    }
  }, [selectedRideType]);

  // Add this useEffect to handle API calls when dropdowns change
  useEffect(() => {
    const checkConfiguration = async () => {
      // Only proceed if we have all required values
      if (!selectedLocation?.id || !selectedRideType || !selectedCarType) {
        return;
      }

      setIsLoading(true);
      try {
        const currentRateType = getRateTypeByRideType(selectedRideType);
        const result = await rateService.getRateCard(
          selectedLocation.id,
          selectedRideType,
          selectedCarType,
          dutyTypeOptions,
          currentRateType
        );

        if (result.success) {
          
          if (result.data.hasExistingConfig) {
            // Preserve selected values while updating form data
           
            setFormData(prev => ({
              ...result.data.formattedData,
              locationId: selectedLocation.id,
              rideType: selectedRideType,
              carType: selectedCarType
            }));
            setRateCardId(result.data.data[0].id);
            setHasExistingConfig(true);
          } else {
            // Reset form but keep selected values
            setFormData(prev => ({
              ...prev,
              locationId: selectedLocation.id,
              rideType: selectedRideType,
              carType: selectedCarType,
              rateType: selectedRideType.startsWith('Ride') || selectedRideType.startsWith('Airport') ? 'km' : 'package',
              baseRate: '',
              surCharge: '',
              platformCharge: '',
              waitingTime: '',
              bufferTime: '',
              extraKmCharge: '',
              graceKm: '',
              extraTimeCharge: '',
              graceTime: '',
              kmCharge: "",
              rateConfig: {
                base_rate_per_km: "",
              },
            }));
            setRateCardId(null);
            setHasExistingConfig(false);
          }
        }
      } catch (error) {
        console.error('Error checking configuration:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkConfiguration();
  }, [selectedLocation?.id, selectedRideType, selectedCarType]);

  const handleFilterChange = async (type, value) => {
    let newRateType = '';

    switch (type) {
      case 'location':
        setSelectedLocation(value);
        setFormData(prev => ({
          ...prev,
          locationId: value?.id || null
        }));
        break;
      case 'rideType':
        setSelectedRideType(value);
        newRateType = (value.startsWith("Ride") || value.startsWith("Airport")) ? "km" : "slab";
        setRateType(newRateType);
        break;
      case 'carType':
        setSelectedCarType(value);
        break;
      default:
        return;
    }
  };

  async function fetchRateCardConfig() {
    // Get current filter values including the new change
    const currentFilters = {
      location: selectedLocation,
      rideType: selectedRideType,
      carType: selectedCarType
    };

    // Call getRateCard if at least one filter has a value
    if (currentFilters.location?.id || currentFilters.rideType || currentFilters.carType) {
      setIsLoading(true);
      try {
        const currentRateType = getRateTypeByRideType(currentFilters.rideType);
        const result = await rateService.getRateCard(
          currentFilters.location || '',
          currentFilters.rideType || '',
          currentFilters.carType || '',
          dutyTypeOptions,
          currentRateType
        );

        if (result.success) {
          setHasExistingConfig(result.data.hasExistingConfig);
          

          if (result.data.hasExistingConfig && result.data.formattedData) {
            // Update form with existing configuration
           
            setFormData(result.data.formattedData);
            setRateCardId(result.data.data[0].id);
          } else {
            // Reset form but keep current filter values
            setFormData(prev => ({
              ...prev,
              locationId: currentFilters.location || prev.locationId,
              rideType: currentFilters.rideType || prev.rideType,
              carType: currentFilters.carType || prev.carType,
              // Use the current ride type to determine rate type if it's a ride type change
              rateType: ((currentFilters.rideType.startsWith("Ride") || currentFilters.rideType.startsWith("Airport")) ? "km" : "package"),
              baseRate: '',
              surCharge: '',
              platformCharge: '',
              waitingTime: '',
              bufferTime: '',
              extraKmCharge: '',
              graceKm: '',
              extraTimeCharge: '',
              graceTime: '',
              kmCharge: "",
              rateConfig: {
                base_rate_per_km: "",
              },
            }));
            setRateCardId(null);
          }
          setHasChanges(false);
        }
      } catch (error) {
        console.error('Error fetching rate card:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Reset form data if no filters are selected
      setFormData(prev => ({
        ...prev,
        locationId: '',
        rideType: '',
        carType: '',
        rateType: '',
        baseRate: '',
        surCharge: '',
        platformCharge: '',
        waitingTime: '',
        bufferTime: '',
        extraKmCharge: '',
        graceKm: '',
        extraTimeCharge: '',
        graceTime: '',
        kmCharge: "",
        rateConfig: {
          base_rate_per_km: "",
        },
      }));
      setHasExistingConfig(false);
      setRateCardId(null);
    }
  }

  const hasInvalidValues = () => {
    const requiredFields = ['baseRate'];

    if (formData.rateType === 'km') {
      requiredFields.push('kmCharge');
    }

    return requiredFields.some(field => {
      const value = parseFloat(formData[field]);
      return isNaN(value) || value <= 0;
    });
  };

  useEffect(() => {
    // Fetch initial rate card configuration when component mounts
    if (selectedLocation || selectedRideType || selectedCarType) {
      fetchRateCardConfig();
    }
  }, [selectedLocation, selectedRideType, selectedCarType]);

  const selectSx = {
    height: '40px',
    '& .MuiSelect-select': {
      padding: '8px 14px',
    }
  };

  // Update the return statement's closing section
  return (
    <div className="city-config-form">
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '20px',
        alignItems: 'flex-start'
      }}>
        <div style={{ flex: 0.5 }}>
          <LocationDropdown
            onChange={(value) => handleFilterChange('location', value?.id)}  // Pass the whole value object
            value={selectedLocation}
            selected={selectedLocation}
            enabledefaultCity={!selectedLocation}  // Only enable default city if no location is selected
          />
        </div>

        <Select
          style={{ flex: 0.3 }}
          size="small"
          sx={selectSx}
          value={selectedRideType}
          onChange={(e) => handleFilterChange('rideType', e.target.value)}
        >
          {rideTypeOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          style={{ flex: 0.3 }}
          size="small"
          sx={selectSx}
          value={selectedCarType}
          onChange={(e) => handleFilterChange('carType', e.target.value)}
        >
          {carOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <div>Loading rate card data...</div>
      ) : (
        <>
          {formData.rateType === 'package' ? (
            <PackageConfig
              initialData={formData}
              onChange={handleFormChange}
              selectedLocation={selectedLocation}
              selectedRideType={selectedRideType}
              selectedCarType={selectedCarType}
              rateCardId={rateCardId}
              isUpdate={hasExistingConfig}
            />
          ) : (
            <>

              <div className="bg-white border border-gray-200 p-4" style={{borderRadius: '8px'}}>
                <FormBuilder
                  formConfig={getFormConfig()}
                  values={formData}
                  onChange={handleFormChange}
                  hideSubmitButton={true}
                  hideSectionStyle={true}
                />

                <div className="mb-4 w-[50%]">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Additional charges configuration
                  </h3>

                  <div className="mb-6">

                    <div className="border border-gray-200 p-4">
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          width: "100%",
                          height: "100%",
                          alignItems: "flex-end",
                        }}
                      >
                        <TextField
                          size="small"
                          value={formData?.bufferTime > 0 ? formData?.bufferTime : ''}
                          onChange={(e) => handleFormChange({'bufferTime': e.target.value})}
                          label="Wait Buffer (Min)"
                          style={{ flex: "1", minWidth: "0" }}
                        />

                        <TextField
                          size="small"
                          value={formData?.waitingTime > 0 ? formData?.waitingTime : ''}
                          onChange={(e) => handleFormChange({'waitingTime': e.target.value})}
                          label="Wait Price (₹/Min)"
                          style={{ flex: "1", minWidth: "0" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="save-button-container" style={{ display: 'flex', gap: '10px' }}>
                {hasExistingConfig ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateFormData}
                    disabled={isLoading || isUpdating || !hasChanges || hasInvalidValues()}
                    sx={{ textTransform: "none" }}
                  >
                    {isUpdating ? 'Updating...' : 'Update Configuration'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveFormData}
                    disabled={isLoading || isSaving || !hasChanges || hasInvalidValues()}
                    sx={{ textTransform: "none" }}
                  >
                    {isSaving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                )}
              </div>
            </>
          )}
          <PeakHourConfig
            locationId={selectedLocation}
            carType={selectedCarType}
            rideType={selectedRideType}
            companyId={""}
            initialPeakHours={peakHours}
          />
        </>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}